import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";

type SelectBooleanValueProps = {
  label: string;
  onOptionSelected?: (option: { label: string; value: boolean } | null) => void;
  required?: boolean;
  enablePortal?: boolean;
  sx?: SxProps;
  options?: { label: string; value: boolean }[];
};

export type RhfSelectBooleanValueProps<T extends FieldValues> =
  SelectBooleanValueProps & UseControllerProps<T>;

const defaultOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false }
];

export default function SelectBooleanValue<T extends FieldValues>({
  control,
  name,
  label,
  onOptionSelected,
  required,
  enablePortal,
  options = defaultOptions,
  sx
}: RhfSelectBooleanValueProps<T>) {
  const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option: { label: string; value: boolean }) => option.label
  });

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      openOnFocus
      autoHighlight
      name={name}
      label={label}
      filterOptions={filterOptions}
      options={options}
      required={required}
      getOptionLabel={(option) => {
        return option.label ?? "";
      }}
      renderOption={(props: object, option: any) => (
        <ListItem {...props}>
          <Stack direction="row" spacing={1}>
            <ListItemText>{option.label}</ListItemText>
          </Stack>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option: any) => {
        if (onOptionSelected) {
          onOptionSelected(option);
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 280, ...sx }}
    />
  );
}
